import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Building2, Loader2, ShieldCheck, UserRoundCheck } from "lucide-react"
import { Alert, AlertDescription } from "shared/ui/alert"
import { Badge } from "shared/ui/badge"
import { Button } from "shared/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "shared/ui/card"
import { Input } from "shared/ui/input"
import { Label } from "shared/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "shared/ui/select"
import { Textarea } from "shared/ui/textarea"
import {
  cancelContractorAzureOnboarding,
  loginWithAzureToken,
  resolveContractorAzureOnboarding,
  submitContractorAzureOnboarding,
  type ContractorAzureOnboardingStateDto,
} from "modules/auth/api/authService"
import { getContractorCompanies, type ContractorCompany } from "modules/permit/api/contractorService"
import { searchPermitSupervisors, type PermitEmployeeLookup } from "modules/permit/api/permitService"

const AZURE_CONTRACTOR_IDTOKEN_KEY = "eptw.contractor.azure.idToken"
const DEFAULT_TENANT_ID = process.env.REACT_APP_DEFAULT_TENANT_ID ?? ""
const CONTRACTOR_POSITION_OPTIONS = [
  "เสี่ยงอันตราย",
  "เจ้าหน้าที่ความปลอดภัย",
  "หัวหน้างาน",
  "Site Manager",
] as const

const statusLabelMap: Record<string, string> = {
  NOT_REGISTERED: "ยังไม่ลงทะเบียน",
  WAITING: "Waiting for Approval",
  ACTIVE: "Active",
}

const statusVariantMap: Record<string, "default" | "secondary"> = {
  NOT_REGISTERED: "secondary",
  WAITING: "secondary",
  ACTIVE: "default",
}

const toUiDateTime = (value?: string | null) => {
  if (!value) return "-"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString("th-TH", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  })
}

const getStoredContractorIdToken = () => {
  try {
    return sessionStorage.getItem(AZURE_CONTRACTOR_IDTOKEN_KEY) ?? ""
  } catch {
    return ""
  }
}

const setStoredContractorIdToken = (token: string) => {
  try {
    sessionStorage.setItem(AZURE_CONTRACTOR_IDTOKEN_KEY, token)
  } catch {
    // ignore storage failures
  }
}

const clearStoredContractorIdToken = () => {
  try {
    sessionStorage.removeItem(AZURE_CONTRACTOR_IDTOKEN_KEY)
  } catch {
    // ignore storage failures
  }
}

export function ContractorAzureOnboardingPage() {
  const navigate = useNavigate()

  const [idToken, setIdToken] = useState("")
  const [state, setState] = useState<ContractorAzureOnboardingStateDto | null>(null)
  const [username, setUsername] = useState("")
  const [fullName, setFullName] = useState("")
  const [position, setPosition] = useState("")
  const [phone, setPhone] = useState("")
  const [remark, setRemark] = useState("")
  const [companyId, setCompanyId] = useState("")
  const [controllerUserId, setControllerUserId] = useState("")
  const [companySearch, setCompanySearch] = useState("")
  const [controllerSearch, setControllerSearch] = useState("")
  const [companies, setCompanies] = useState<ContractorCompany[]>([])
  const [controllers, setControllers] = useState<PermitEmployeeLookup[]>([])
  const [isBootstrapping, setIsBootstrapping] = useState(true)
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false)
  const [isLoadingControllers, setIsLoadingControllers] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const tenantId = useMemo(() => state?.tenantId ?? DEFAULT_TENANT_ID, [state?.tenantId])
  const isWaiting = state?.status?.toUpperCase() === "WAITING"

  useEffect(() => {
    let cancelled = false

    const run = async () => {
      const token = getStoredContractorIdToken()
      if (!token) {
        setError("Missing Azure session. Please login again.")
        setIsBootstrapping(false)
        return
      }

      setIdToken(token)
      setStoredContractorIdToken(token)

      try {
        const resolved = await resolveContractorAzureOnboarding({ idToken: token })
        if (cancelled) return

        setState(resolved)
        setUsername(resolved.username ?? resolved.email ?? "")
        setFullName(resolved.fullName ?? "")
        setPosition(resolved.position ?? "")
        setPhone(resolved.phone ?? "")
        setRemark(resolved.remark ?? "")
        setCompanyId(resolved.companyId ?? "")
        setControllerUserId(resolved.controllerUserId ?? "")

        if ((resolved.status ?? "").toUpperCase() === "ACTIVE") {
          const response = await loginWithAzureToken({ idToken: token, userType: "CONTRACTOR" })
          if (cancelled) return
          clearStoredContractorIdToken()
          navigate("/modules", { replace: true, state: { user: { ...response, userType: "contractor" } } })
          return
        }
      } catch (err: any) {
        if (cancelled) return
        const message = err?.response?.data ?? err?.message ?? "Failed to resolve onboarding state"
        setError(typeof message === "string" ? message : "Failed to resolve onboarding state")
      } finally {
        if (!cancelled) {
          setIsBootstrapping(false)
        }
      }
    }

    void run()

    return () => {
      cancelled = true
    }
  }, [navigate])

  useEffect(() => {
    let cancelled = false
    const timer = window.setTimeout(async () => {
      setIsLoadingCompanies(true)
      try {
        const result = await getContractorCompanies({
          tenantId: tenantId || undefined,
          search: companySearch.trim() || undefined,
          pageNumber: 1,
          pageSize: 50,
        })
        if (!cancelled) {
          setCompanies(result.items ?? [])
        }
      } catch {
        if (!cancelled) {
          setCompanies([])
        }
      } finally {
        if (!cancelled) {
          setIsLoadingCompanies(false)
        }
      }
    }, 300)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [companySearch, tenantId])

  useEffect(() => {
    let cancelled = false
    const timer = window.setTimeout(async () => {
      setIsLoadingControllers(true)
      try {
        // Intentionally do not scope controller search by tenant so contractors can find cross-tenant controllers.
        const result = await searchPermitSupervisors(
          undefined,
          controllerSearch.trim() || undefined,
          1,
          50,
        )
        if (!cancelled) {
          setControllers(result.items ?? [])
        }
      } catch {
        if (!cancelled) {
          setControllers([])
        }
      } finally {
        if (!cancelled) {
          setIsLoadingControllers(false)
        }
      }
    }, 300)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [controllerSearch])

  const handleSubmit = async () => {
    setError(null)

    if (!username.trim()) {
      setError("กรุณาระบุชื่อผู้ใช้งาน")
      return
    }

    if (!idToken) {
      setError("Missing Azure session. Please login again.")
      return
    }

    if (!companyId) {
      setError("กรุณาเลือกบริษัทผู้รับเหมา")
      return
    }

    if (!controllerUserId) {
      setError("กรุณาเลือกผู้ควบคุมงาน")
      return
    }

    setIsSubmitting(true)
    try {
      const result = await submitContractorAzureOnboarding({
        idToken,
        username: username.trim(),
        fullName: fullName.trim() || undefined,
        position: position || undefined,
        phone: phone.trim() || undefined,
        remark: remark.trim() || undefined,
        tenantId: tenantId || undefined,
        companyId,
        controllerUserId,
      })
      setState(result)
      setUsername(result.username ?? username.trim())
      setFullName(result.fullName ?? fullName)
      setPosition(result.position ?? position)
      setPhone(result.phone ?? phone)
      setRemark(result.remark ?? remark)
      setCompanyId(result.companyId ?? companyId)
      setControllerUserId(result.controllerUserId ?? controllerUserId)
    } catch (err: any) {
      const message = err?.response?.data ?? err?.message ?? "Submit onboarding failed"
      setError(typeof message === "string" ? message : "Submit onboarding failed")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancelOnboarding = async () => {
    if (!idToken) {
      setError("Missing Azure session. Please login again.")
      return
    }

    setError(null)
    setIsCancelling(true)
    try {
      const result = await cancelContractorAzureOnboarding({ idToken })
      setState(result)
      setUsername(result.username ?? state?.username ?? "")
      setFullName(result.fullName ?? state?.fullName ?? "")
      setPosition(result.position ?? state?.position ?? "")
      setPhone(result.phone ?? state?.phone ?? "")
      setRemark(result.remark ?? state?.remark ?? "")
      setCompanyId(result.companyId ?? "")
      setControllerUserId(result.controllerUserId ?? "")
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setState((prev) =>
          prev
            ? {
                ...prev,
                status: "NOT_REGISTERED",
                message: "Backend cancel endpoint not found, switched to edit mode.",
              }
            : prev,
        )
        setError("API ยังไม่อัปเดต endpoint cancel (404) แต่ปลดล็อกให้แก้ไขข้อมูลได้แล้ว")
        return
      }
      const message = err?.response?.data ?? err?.message ?? "Cancel onboarding failed"
      setError(typeof message === "string" ? message : "Cancel onboarding failed")
    } finally {
      setIsCancelling(false)
    }
  }

  const selectedCompany = companies.find((item) => item.companyId === companyId)
  const selectedController = controllers.find((item) => item.userId === controllerUserId)

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-100 p-6">
      <div className="mx-auto w-full max-w-3xl">
        <Card className="border-orange-100 bg-white/95 shadow-xl">
          <CardHeader className="space-y-3">
            <CardTitle className="flex items-center gap-2 text-2xl text-orange-900">
              <UserRoundCheck className="h-6 w-6 text-orange-600" />
              Contractor Azure Onboarding
            </CardTitle>
            <p className="text-sm text-gray-600">
              หากยังไม่มีบัญชีในระบบ กรุณากรอกข้อมูลเพื่อลงทะเบียนและรอการอนุมัติจากผู้ดูแลระบบ
            </p>
          </CardHeader>
          <CardContent className="space-y-5">
            {isBootstrapping ? (
              <div className="flex items-center gap-2 rounded border border-orange-100 bg-orange-50 p-4 text-sm text-orange-700">
                <Loader2 className="h-4 w-4 animate-spin" />
                กำลังตรวจสอบข้อมูลผู้ใช้งาน...
              </div>
            ) : null}

            {error ? (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1">
                <Label>Username</Label>
                <Input value={username} readOnly disabled placeholder="ระบุชื่อผู้ใช้งาน" />
              </div>
              <div className="space-y-1">
                <Label>Email</Label>
                <Input value={state?.email ?? "-"} disabled />
              </div>
              <div className="space-y-1">
                <Label>ชื่อ - นามสกุล ของผู้ใช้งาน *</Label>
                <Input
                  value={fullName}
                  onChange={(event) => setFullName(event.target.value)}
                  disabled={isBootstrapping || isWaiting}
                  placeholder="ระบุชื่อผู้ใช้งาน"
                />
              </div>
              <div className="space-y-1">
                <Label>สถานะ</Label>
                <div className="flex h-10 items-center rounded border bg-gray-50 px-3">
                  <Badge variant={statusVariantMap[(state?.status ?? "").toUpperCase()] ?? "secondary"}>
                    {statusLabelMap[(state?.status ?? "").toUpperCase()] ?? (state?.status ?? "-")}
                  </Badge>
                </div>
              </div>
            </div>

            {state?.createdAt ? (
              <div className="text-xs text-gray-500">ส่งคำขอล่าสุดเมื่อ: {toUiDateTime(state.createdAt)}</div>
            ) : null}

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>บริษัทผู้รับเหมา *</Label>
                <Select value={companyId} onValueChange={setCompanyId} disabled={isBootstrapping || isWaiting}>
                  <SelectTrigger>
                    <SelectValue placeholder="เลือกบริษัทผู้รับเหมา" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72 p-0">
                    <div className="sticky top-0 z-10 border-b bg-white p-2">
                      <Input
                        value={companySearch}
                        onChange={(event) => setCompanySearch(event.target.value)}
                        placeholder="ค้นหาบริษัท"
                        onKeyDown={(event) => event.stopPropagation()}
                        onPointerDown={(event) => event.stopPropagation()}
                      />
                    </div>
                    {companyId && !companies.some((item) => item.companyId === companyId) ? (
                      <SelectItem value={companyId}>{state?.companyName ?? companyId}</SelectItem>
                    ) : null}
                    {isLoadingCompanies ? (
                      <div className="px-3 py-2 text-sm text-gray-500">กำลังโหลด...</div>
                    ) : companies.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-500">ไม่พบข้อมูล</div>
                    ) : (
                      companies.map((item) => (
                        <SelectItem key={item.companyId} value={item.companyId}>
                          {item.companyName}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Controller (ผู้ควบคุมงาน) *</Label>
                <Select
                  value={controllerUserId}
                  onValueChange={setControllerUserId}
                  disabled={isBootstrapping || isWaiting}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="เลือกผู้ควบคุมงาน" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72 p-0">
                    <div className="sticky top-0 z-10 border-b bg-white p-2">
                      <Input
                        value={controllerSearch}
                        onChange={(event) => setControllerSearch(event.target.value)}
                        placeholder="ค้นหาผู้ควบคุมงาน / employee id"
                        onKeyDown={(event) => event.stopPropagation()}
                        onPointerDown={(event) => event.stopPropagation()}
                      />
                    </div>
                    {controllerUserId && !controllers.some((item) => item.userId === controllerUserId) ? (
                      <SelectItem value={controllerUserId}>{state?.controllerName ?? "ผู้ควบคุมงานที่เลือกไว้"}</SelectItem>
                    ) : null}
                    {isLoadingControllers ? (
                      <div className="px-3 py-2 text-sm text-gray-500">กำลังโหลด...</div>
                    ) : controllers.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-500">ไม่พบข้อมูล</div>
                    ) : (
                      controllers
                        .filter((item) => Boolean(item.userId))
                        .map((item) => {
                          const affiliation = item.department?.trim() || item.position?.trim() || ""
                          const label = affiliation ? `${item.employeeName} - ${affiliation}` : item.employeeName
                          return (
                            <SelectItem key={item.userId} value={item.userId!}>
                              {label}
                            </SelectItem>
                          )
                        })
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>หน้าที่ของผู้ติดต่อ</Label>
                <Select value={position} onValueChange={setPosition} disabled={isBootstrapping || isWaiting}>
                  <SelectTrigger>
                    <SelectValue placeholder="เลือกหน้าที่ของผู้ติดต่อ" />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTRACTOR_POSITION_OPTIONS.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>เบอร์โทรศัพท์ที่ติดต่อ</Label>
                <Input
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                  disabled={isBootstrapping || isWaiting}
                  placeholder="ระบุเบอร์โทรศัพท์"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>หมายเหตุ</Label>
              <Textarea
                value={remark}
                onChange={(event) => setRemark(event.target.value)}
                disabled={isBootstrapping || isWaiting}
                placeholder="กรอกรายละเอียดเพิ่มเติม"
                rows={4}
              />
            </div>

            {isWaiting ? (
              <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                ระบบได้รับคำขอลงทะเบียนแล้ว กรุณารอ ผู้ควบคุมงานของท่าน ดำเนินการอนุมัติบัญชีเข้าใช้งาน
              </div>
            ) : null}

            <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  clearStoredContractorIdToken()
                  navigate("/login/contractor")
                }}
              >
                กลับหน้า Contractor Login
              </Button>

              <div className="flex items-center gap-2">
                {isWaiting ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancelOnboarding}
                    disabled={isBootstrapping || isCancelling}
                  >
                    {isCancelling ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    ยกเลิกการขอลงทะเบียน
                  </Button>
                ) : null}

                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isBootstrapping || isSubmitting || isWaiting}
                  className="gap-2"
                >
                  {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                  ส่งคำขอลงทะเบียน
                </Button>
              </div>
            </div>

            {selectedCompany || selectedController ? (
              <div className="grid gap-3 rounded border bg-gray-50 p-3 text-sm text-gray-700 md:grid-cols-2">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-gray-500" />
                  <span>บริษัท: {selectedCompany?.companyName ?? state?.companyName ?? "-"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <UserRoundCheck className="h-4 w-4 text-gray-500" />
                  <span>ผู้ควบคุมงาน: {selectedController?.employeeName ?? state?.controllerName ?? "-"}</span>
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default ContractorAzureOnboardingPage
